import { useEffect, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface OwlSpotlightLeg {
  fixture_id: string;
  home_team: string;
  away_team: string;
  league: string;
  kickoff_at: string;
  market: "result_btts" | "multiscores" | "btts" | "ou" | "match_winner";
  selection: string;
  odds: number;
  source?: "sportybet_gh" | "the_odds_api";
  bookmaker?: string;
  outcome: "win" | "loss" | null;
}

export interface OwlSpotlightSlip {
  session_date: string;
  status: "published" | "settled";
  selections: OwlSpotlightLeg[];
  combined_odds: number;
  result: "win" | "loss" | null;
}

export function useOwlSpotlight(sessionDate: string): OwlSpotlightSlip | null {
  const { user } = useAuthSession();
  const [slip, setSlip] = useState<OwlSpotlightSlip | null>(null);

  useEffect(() => {
    if (!user) {
      setSlip(null);
      return;
    }
    let cancelled = false;
    const read = async () => {
      const client = getBrowserClient() as any;
      const { data, error } = await client.from("owl_spotlight_slips")
        .select("session_date,status,selections,combined_odds,result")
        .eq("session_date", sessionDate).maybeSingle();
      if (cancelled) return;
      if (error || !data || !Array.isArray(data.selections) || data.selections.length !== 2) {
        setSlip(null);
        return;
      }
      setSlip({
        session_date: data.session_date,
        status: data.status,
        selections: data.selections,
        combined_odds: Number(data.combined_odds),
        result: data.result,
      });
    };
    void read();
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") void read(); }, 120_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [user?.id, sessionDate]);

  return slip;
}
